import { useState } from "react";
import { Link } from "react-router";
import dayjs from "dayjs";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { formatMoneyCompact } from "@/lib/format";
import { useSuretyClients } from "@/hooks/queries/useSuretyClients";
import {
  useAddSuretyClients,
  useRemoveSuretyClient,
} from "@/hooks/mutations/useSuretyClientMutations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { EmptyNote, PageHeader } from "@/components/surety/SuretyUI";

/** Ventana para pegar RUCs, uno por línea o separados por comas. */
function AddClientsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [text, setText] = useState("");
  const mutation = useAddSuretyClients();

  function submit() {
    const rucs = text.split(/[\s,;]+/).filter(Boolean);
    if (rucs.length === 0) return;
    mutation.mutate(rucs, {
      onSuccess: ({ found, notFound }) => {
        toast.success(`${found} empresa${found === 1 ? "" : "s"} en tu cartera`);
        if (notFound.length) {
          toast.warning(
            `${notFound.length} RUC sin datos todavía (nunca ganaron una obra): ${notFound.slice(0, 5).join(", ")}`,
          );
        }
        setText("");
        onOpenChange(false);
      },
      onError: () => toast.error("No se pudieron agregar los RUC"),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agregar clientes</DialogTitle>
          <DialogDescription>
            Pega los RUC de tus clientes, uno por línea. Te avisamos cuando ganen, también
            dentro de un consorcio.
          </DialogDescription>
        </DialogHeader>
        <label htmlFor="surety-rucs" className="text-sm font-semibold">
          RUC
        </label>
        <Textarea
          id="surety-rucs"
          rows={8}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={"20603194722\n20480522827"}
        />
        <DialogFooter>
          <button
            type="button"
            onClick={submit}
            disabled={mutation.isPending || !text.trim()}
            className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            Agregar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** La cartera: cada cliente con lo último que ganó, su capacidad y antecedentes. */
export default function SuretyClients() {
  const { data, isLoading, isError } = useSuretyClients();
  const { data: activeOrg } = authClient.useActiveOrganization();
  const remove = useRemoveSuretyClient();
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");

  const clients = (data ?? []).filter((client) => {
    const term = query.trim().toLowerCase();
    return !term || client.name.toLowerCase().includes(term) || client.ruc.includes(term);
  });
  const withRecentWin = (data ?? []).filter(
    (client) =>
      client.lastAward && dayjs().diff(dayjs(client.lastAward.awardedAt), "day") <= 7,
  ).length;

  return (
    <div className="mx-auto w-full flex-1 px-4 py-6 md:px-14">
      <PageHeader
        eyebrow={activeOrg?.name}
        title="Clientes"
        subtitle={
          data
            ? `${data.length} empresas en tu cartera · ${withRecentWin} ganaron esta semana`
            : "Las empresas de tu cartera"
        }
        actions={
          <>
            <label htmlFor="surety-search" className="sr-only">
              Buscar por nombre o RUC
            </label>
            <input
              id="surety-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Nombre o RUC"
              className="h-10 rounded-xl border bg-card px-3 text-sm outline-none focus:border-primary"
            />
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-xl bg-primary px-4 text-sm font-bold text-primary-foreground"
            >
              <Plus className="size-4" />
              Agregar RUC
            </button>
          </>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : isError ? (
        <EmptyNote>No se pudo cargar la cartera.</EmptyNote>
      ) : (data ?? []).length === 0 ? (
        <EmptyNote>Tu cartera está vacía. Agrega los RUC de tus clientes para empezar.</EmptyNote>
      ) : (
        <div className="overflow-x-auto rounded-2xl border bg-card shadow-sm">
          <table className="w-full min-w-[960px] text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-slate-500">
                <th scope="col" className="px-5 py-3 font-semibold">Empresa</th>
                <th scope="col" className="px-3 py-3 font-semibold">Última buena pro</th>
                <th scope="col" className="px-3 py-3 text-right font-semibold">Buenas pro 6 meses</th>
                <th scope="col" className="px-3 py-3 text-right font-semibold">Capacidad máx.</th>
                <th scope="col" className="w-48 px-3 py-3 font-semibold">Capacidad libre</th>
                <th scope="col" className="px-3 py-3 font-semibold">Antecedentes</th>
                <th scope="col" className="px-5 py-3"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => {
                const { profile } = client;
                const freeShare =
                  profile.maxCapacity && profile.freeCapacity !== null
                    ? profile.freeCapacity / profile.maxCapacity
                    : null;
                const recent =
                  client.lastAward &&
                  dayjs().diff(dayjs(client.lastAward.awardedAt), "day") <= 7;
                return (
                  <tr key={client.companyId} className="border-b align-top last:border-0">
                    <td className="px-5 py-3">
                      <Link
                        to={`/companies/${client.companyId}`}
                        className="cursor-pointer font-bold text-slate-900 hover:text-primary dark:text-slate-50"
                      >
                        {client.name}
                      </Link>
                      <span className="block text-xs text-slate-500">
                        {[client.ruc, profile.department].filter(Boolean).join(" · ")}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      {client.lastAward ? (
                        <>
                          <span
                            className={cn(
                              "inline-block rounded-full px-2.5 py-0.5 text-xs font-bold",
                              recent
                                ? "bg-blue-50 text-blue-700"
                                : "bg-slate-100 text-slate-600",
                            )}
                          >
                            {dayjs(client.lastAward.awardedAt).format("DD MMM YYYY")}
                          </span>
                          <span className="block text-xs text-slate-500">
                            {client.lastAward.entity} · {formatMoneyCompact(client.lastAward.amount)}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">Sin buenas pro en 6 meses</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right font-semibold">{client.awards6m}</td>
                    <td className="px-3 py-3 text-right">{formatMoneyCompact(profile.maxCapacity)}</td>
                    <td className="px-3 py-3">
                      {freeShare !== null ? (
                        <div className="flex flex-col gap-1">
                          <span>
                            {formatMoneyCompact(profile.freeCapacity)} · {Math.round(freeShare * 100)}%
                          </span>
                          <span aria-hidden className="block h-1.5 rounded-full bg-slate-100">
                            <span
                              className={cn(
                                "block h-1.5 rounded-full",
                                freeShare < 0.2 ? "bg-red-600" : freeShare < 0.5 ? "bg-amber-600" : "bg-emerald-600",
                              )}
                              style={{ width: `${Math.max(2, freeShare * 100)}%` }}
                            />
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {profile.activeSanctions > 0 ? (
                        <span className="font-bold text-red-600">Sanción vigente</span>
                      ) : profile.sanctions > 0 ? (
                        <span>{profile.sanctions} sanción histórica</span>
                      ) : (
                        <span className="text-emerald-700">Sin sanciones</span>
                      )}
                      {profile.penalties > 0 && (
                        <span className="block text-xs text-slate-500">
                          {profile.penalties} penalidad{profile.penalties === 1 ? "" : "es"}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        aria-label={`Quitar ${client.name} de la cartera`}
                        disabled={remove.isPending}
                        onClick={() =>
                          remove.mutate(client.companyId, {
                            onError: () => toast.error("No se pudo quitar el cliente"),
                          })
                        }
                        className="inline-flex size-9 cursor-pointer items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="border-t px-5 py-3 text-xs text-slate-500">
            Capacidad de contratación y antecedentes: ficha del proveedor del OECE.
          </p>
        </div>
      )}

      <AddClientsDialog open={adding} onOpenChange={setAdding} />
    </div>
  );
}
